import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Gift, ArrowUpRight } from "lucide-react";
import { api } from "../lib/api";
import { Link } from "react-router-dom";
const HOLIDAY_FALLBACK = "https://images.unsplash.com/photo-1501200291289-c5a76c232e5f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const backend = process.env.REACT_APP_BACKEND_URL || "";
export const resolveImg = (u) => (u && u.startsWith("/api/") ? `${backend}${u}` : u);

let cache = null;
export function useHoliday() {
  const [h, setH] = useState(cache);
  useEffect(() => {
    api.get("/holiday").then(({ data }) => { cache = data; setH(data); }).catch(() => {});
  }, []);
  return h;
}

export function HolidayCtaButton({ testid, className = "" }) {
  const h = useHoliday();
  if (!h?.enabled) return null;
  return (
    <Link
      to="/holiday-menu"
      data-testid={testid}
      className={`holiday-cta inline-flex items-center gap-2 border-2 border-bone bg-[#b3261e] px-6 py-4 font-mono text-xs uppercase tracking-[0.24em] text-bone transition-transform duration-200 hover:-translate-y-0.5 ${className}`}
    >
      <Gift size={16} /> {h.button_label || "Holiday Menu"}
    </Link>
  );
}

export function HolidayImages({ h }) {
  const images = h.images?.length ? h.images.map(resolveImg) : [HOLIDAY_FALLBACK];
  return (
    <div className={`grid gap-3 ${images.length > 1 ? "grid-cols-2" : "grid-cols-1"}`} data-testid="holiday-images">
      {images.slice(0, 4).map((src, i) => (
        <motion.div
          key={src + i}
          initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
          className={`overflow-hidden border-2 border-bone shadow-hard ${images.length === 3 && i === 0 ? "col-span-2" : ""}`}
        >
          <img src={src} alt={`${h.title} ${i + 1}`} className={images.length === 1 ? "h-auto w-full" : "aspect-[3/4] h-full w-full object-cover"} />
        </motion.div>
      ))}
    </div>
  );
}

export function HolidaySection() {
  const h = useHoliday();
  if (!h?.enabled) return null;
  return (
    <section className="relative overflow-hidden border-b border-ink bg-[#b3261e] py-14 text-bone md:py-20" data-testid="holiday-section">
      <div className="grain-block absolute inset-0 opacity-40" />
      <div className="relative mx-auto grid max-w-[1400px] grid-cols-1 items-center gap-10 px-4 md:grid-cols-12 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
          className="md:col-span-6"
        >
          <div className="eyebrow mb-3 flex items-center gap-2 text-bone/80"><Gift size={14} /> {h.eyebrow}</div>
          <h2 className="font-rye text-4xl uppercase leading-[0.95] sm:text-5xl lg:text-6xl" data-testid="holiday-title">{h.title}</h2>
          {h.subtitle && <p className="mt-5 max-w-xl font-serif text-base leading-relaxed text-bone/90 md:text-lg">{h.subtitle}</p>}
          {h.items?.length > 0 && (
            <ul className="mt-6 max-w-xl divide-y divide-bone/30 border-y border-bone/30" data-testid="holiday-items">
              {h.items.map((it) => (
                <li key={it.id} className="flex items-baseline gap-3 py-3">
                  <div className="flex-1">
                    <div className="font-alfa text-lg uppercase leading-tight">{it.name}</div>
                    {it.desc && <div className="font-serif text-sm text-bone/80">{it.desc}</div>}
                  </div>
                  {it.price && <div className="font-mono text-sm tabular-nums">{it.price}</div>}
                </li>
              ))}
            </ul>
          )}
          <Link
            to="/holiday-menu"
            data-testid="holiday-section-order-btn"
            className="mt-8 inline-flex items-center gap-2 border-2 border-bone bg-bone px-8 py-4 font-alfa text-lg uppercase tracking-[0.18em] text-ink transition-colors hover:bg-transparent hover:text-bone"
          >
            Order {h.button_label || "Holiday Menu"} <ArrowUpRight size={18} />
          </Link>
        </motion.div>
        <div className="md:col-span-6"><HolidayImages h={h} /></div>
      </div>
    </section>
  );
}
